import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Designers from "./pages/Designers";
import WeeklyScores from "./pages/WeeklyScores";
import PeriodScores from "./pages/PeriodScores";
import Performance from "./pages/Performance";
import PerformanceDetail from "./pages/PerformanceDetail";
import StackRanking from "./pages/StackRanking";
import Analytics from "./pages/Analytics";
import Alerts from "./pages/Alerts";
import Screenshots from "./pages/Screenshots";
import ArtistPortal from "./pages/ArtistPortal";
import Goals from "./pages/Goals";
import RoleResponsibilities from "./pages/RoleResponsibilities";
import Achievements from "./pages/Achievements";
import BulkImport from "./pages/BulkImport";
import OrgChart from "./pages/OrgChart";
import UserManagement from "./pages/UserManagement";
import Awards from "./pages/Awards";

// Artist Portal Pages
import ArtistDashboard from "./pages/ArtistDashboard";
import ArtistProfile from "./pages/ArtistProfile";
import ArtistTasks from "./pages/ArtistTasks";
import ArtistPlans from "./pages/ArtistPlans";
import ArtistFeedback from "./pages/ArtistFeedback";
import ArtistAttendance from "./pages/ArtistAttendance";
import ArtistGoals from "./pages/ArtistGoals";
import ArtistAchievements from "./pages/ArtistAchievements";

// Layouts
import AdminLayout from "./components/layout/AdminLayout";
import ArtistLayout from "./components/layout/ArtistLayout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F6F6] dark:bg-slate-900">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    if (user.role === 'artist') {
      return <Navigate to="/artist" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Admin Route Wrapper
const AdminRoute = ({ children }) => {
  return (
    <ProtectedRoute allowedRoles={['admin', 'manager', 'lead']}>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
};

// Root Redirect based on role
const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F6F6] dark:bg-slate-900">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'artist') {
    return <Navigate to="/artist" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={user ? <RootRedirect /> : <Login />} />

      {/* Root Redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Artist Portal Routes */}
      <Route
        path="/artist"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/profile"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/screenshots"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistPortal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/roles"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <RoleResponsibilities artistView={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/achievements"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistAchievements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/goals"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistGoals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/feedback"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistFeedback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/attendance"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistAttendance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/tasks"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistTasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/plans"
        element={
          <ProtectedRoute allowedRoles={['artist', 'viewer']}>
            <ArtistPlans />
          </ProtectedRoute>
        }
      />

      {/* Admin/Manager/Lead Routes */}
      <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
      <Route path="/designers" element={<AdminRoute><Designers /></AdminRoute>} />
      <Route path="/bulk-import" element={<AdminRoute><BulkImport /></AdminRoute>} />
      <Route path="/scores" element={<AdminRoute><WeeklyScores /></AdminRoute>} />
      <Route path="/scores-6month" element={<AdminRoute><PeriodScores defaultPeriod={6} /></AdminRoute>} />
      <Route path="/scores-12month" element={<AdminRoute><PeriodScores defaultPeriod={12} /></AdminRoute>} />
      <Route path="/performance" element={<AdminRoute><Performance /></AdminRoute>} />
      <Route path="/performance/:designerId" element={<AdminRoute><PerformanceDetail /></AdminRoute>} />
      <Route path="/stack-ranking" element={<AdminRoute><StackRanking /></AdminRoute>} />
      <Route path="/awards" element={<AdminRoute><Awards /></AdminRoute>} />
      <Route path="/analytics" element={<AdminRoute><Analytics /></AdminRoute>} />
      <Route path="/alerts" element={<AdminRoute><Alerts /></AdminRoute>} />
      <Route path="/screenshots" element={<AdminRoute><Screenshots /></AdminRoute>} />
      <Route path="/goals" element={<AdminRoute><Goals /></AdminRoute>} />
      <Route path="/roles" element={<AdminRoute><RoleResponsibilities /></AdminRoute>} />
      <Route path="/achievements" element={<AdminRoute><Achievements /></AdminRoute>} />
      <Route path="/org-chart" element={<AdminRoute><OrgChart /></AdminRoute>} />
      <Route path="/users" element={<AdminRoute><UserManagement /></AdminRoute>} />

      {/* Catch all - redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
